import { createApi, retry } from '@reduxjs/toolkit/dist/query/react';
import { axiosBaseQuery } from '@urc-ui/store/api/base';
import { HttpMethod, Tag } from '@urc-ui/store/api/types';
import { getLocaleheader, getMarketplaceIdHeader, resolveAdvertisingApiBaseUrl } from '@urc-ui/store/api/ross/utils';
import {
  CreateFlexibleReportConfigurationRequest,
  DeleteFlexibleReportConfigurationRequest,
  DeleteFlexibleReportConfigurationResponse,
  GetFlexibleReportConfigurationRequest,
  ListFlexibleReportConfigurationsRequest,
  ListFlexibleReportConfigurationsResponse,
  UpdateFlexibleReportConfigurationRequest,
  UpdateFlexibleReportConfigurationResponse,
  UpdateFlexibleReportConfigurationStatusRequest,
  createFlexibleReportConfigurationV2Request
} from '@flexible-reports/store/api/subscriptions/api-types';
import { exponentialBackoffWithJitter, getFrbAuthorizationHeaders } from '@flexible-reports/store/api/base';
import {
  transformCreateReportSuccessResponse,
  transformGetFlexibleReportByIdResponse
} from '@flexible-reports/store/api/subscriptions/transformers/transformFromApiResponse';
import { transformReportConfigurationsToSubscriptions } from '@flexible-reports/store/api/subscriptions/transformers/transform-to-flexible-subscription';
import { FlexibleSubscription } from '@flexible-reports/store/slices/types';
import type { ApiErrorResponse } from '@flexible-reports/store/api/error-mapping/api-error-types';
import { mapApiErrorToServerCode } from '@flexible-reports/store/api/error-mapping/error-code-mapping';
import { createServerError } from '@flexible-reports/store/api/error-mapping/error-validation-factory';
import { ServerValidationError } from '../server-error-codes';
import { CreateApiSuccessResponse } from '@flexible-reports/pages/create-subscription/create-button/types';

export const flexibleSubscriptionsApi = createApi({
  reducerPath: 'flexibleSubscriptionsApi',
  baseQuery: retry(
    axiosBaseQuery({
      baseUrl: resolveAdvertisingApiBaseUrl()
    }),
    exponentialBackoffWithJitter({ baseDelay: 50, maxDelay: 5000, maxRetries: 5 })
  ),
  tagTypes: [Tag.FLEXIBLE_SUBSCRIPTIONS],
  endpoints: builder => ({
    listFlexibleReportConfigurations: builder.query({
      query: (request: ListFlexibleReportConfigurationsRequest) => {
        return {
          url: `/adsApi/v1/query/reportConfigurations`,
          method: HttpMethod.POST,
          headers: { ...getFrbAuthorizationHeaders(), ...getLocaleheader() },
          data: request
        };
      },
      transformResponse: (response: ListFlexibleReportConfigurationsResponse) => {
        return {
          subscriptions: transformReportConfigurationsToSubscriptions(response.reportConfigurations),
          nextToken: response.nextToken
        };
      },
      providesTags: [{ type: Tag.FLEXIBLE_SUBSCRIPTIONS as const }]
    }),
    createFlexibleReportConfiguration: builder.mutation<CreateApiSuccessResponse, CreateFlexibleReportConfigurationRequest>({
      query: (request: CreateFlexibleReportConfigurationRequest) => {
        return {
          url: `/adsApi/v1/create/reportConfigurations`,
          method: HttpMethod.POST,
          headers: { ...getFrbAuthorizationHeaders(), ...getMarketplaceIdHeader(), ...getLocaleheader() },
          data: request
        };
      },
      transformResponse: transformCreateReportSuccessResponse,
      transformErrorResponse: (error: ApiErrorResponse): ServerValidationError =>
        createServerError(mapApiErrorToServerCode(error), error.status, error)
    }),
    getFlexibleReportConfiguration: builder.query<FlexibleSubscription, GetFlexibleReportConfigurationRequest>({
      query: (request: GetFlexibleReportConfigurationRequest) => {
        return {
          url: `/adsApi/v1/retrieve/reportConfigurations`,
          method: HttpMethod.POST,
          headers: {
            ...getFrbAuthorizationHeaders(),
            ...getMarketplaceIdHeader(),
            ...getLocaleheader()
          },
          data: { reportConfigurationIds: [request.reportConfigurationId] }
        };
      },
      transformResponse: transformGetFlexibleReportByIdResponse,
      transformErrorResponse: (error: ApiErrorResponse) => createServerError(mapApiErrorToServerCode(error), error.status, error),
      providesTags: (result, error, request) =>
        result
          ? [
              {
                type: Tag.FLEXIBLE_SUBSCRIPTIONS,
                id: request.reportConfigurationId
              }
            ]
          : []
    }),
    updateFlexibleReportConfiguration: builder.mutation({
      query: (request: UpdateFlexibleReportConfigurationRequest) => {
        return {
          url: `/adsApi/v1/update/reportConfigurations`,
          method: HttpMethod.POST,
          headers: { ...getFrbAuthorizationHeaders(), ...getLocaleheader() },
          data: request
        };
      },
      transformResponse: (response: UpdateFlexibleReportConfigurationResponse) => response,
      transformErrorResponse: (error: ApiErrorResponse) => createServerError(mapApiErrorToServerCode(error), error.status, error),
      invalidatesTags: response =>
        response?.success
          ? response.success.map(success => ({ type: Tag.FLEXIBLE_SUBSCRIPTIONS, id: success.reportConfiguration.reportConfigurationId }))
          : []
    }),
    updateFlexibleReportConfigurationStatus: builder.mutation({
      query: (request: UpdateFlexibleReportConfigurationStatusRequest) => {
        return {
          url: `/adsApi/v1/update/reportConfigurations`,
          method: HttpMethod.POST,
          headers: { ...getFrbAuthorizationHeaders(), ...getLocaleheader() },
          data: request
        };
      },
      transformResponse: (response: UpdateFlexibleReportConfigurationResponse) => response,
      transformErrorResponse: (error: ApiErrorResponse) => createServerError(mapApiErrorToServerCode(error), error.status, error),
      invalidatesTags: response => (response?.success ? [{ type: Tag.FLEXIBLE_SUBSCRIPTIONS as const }] : []),
      async onQueryStarted(request: UpdateFlexibleReportConfigurationStatusRequest, { dispatch, getState, queryFulfilled }) {
        // Get all cached list queries
        const queries = flexibleSubscriptionsApi.util
          .selectInvalidatedBy(getState(), [{ type: Tag.FLEXIBLE_SUBSCRIPTIONS }])
          .filter(query => query.endpointName === 'listFlexibleReportConfigurations');

        // Apply optimistic update to all matching queries
        const patchResults = queries.map(query =>
          dispatch(
            flexibleSubscriptionsApi.util.updateQueryData('listFlexibleReportConfigurations', query.originalArgs, draft => {
              draft.subscriptions = draft.subscriptions.map(subscription => {
                const requestConfig = request.reportConfigurations.find(config => config.reportConfigurationId === subscription.id);
                if (!requestConfig) return subscription;
                return {
                  ...subscription,
                  state: requestConfig.status
                };
              });
            })
          )
        );

        try {
          await queryFulfilled;
        } catch {
          // Undo all optimistic updates on error
          patchResults.forEach(patch => patch.undo());
        }
      }
    }),
    deleteFlexibleReportConfiguration: builder.mutation({
      query: (request: DeleteFlexibleReportConfigurationRequest) => {
        return {
          url: `/adsApi/v1/delete/reportConfigurations`,
          method: HttpMethod.POST,
          headers: { ...getFrbAuthorizationHeaders(), ...getLocaleheader() },
          data: request
        };
      },
      transformResponse: (response: DeleteFlexibleReportConfigurationResponse) => response,
      transformErrorResponse: (error: ApiErrorResponse) => createServerError(mapApiErrorToServerCode(error), error.status, error)
    }),
    createFlexibleReportConfigurationV2: builder.mutation<CreateApiSuccessResponse, createFlexibleReportConfigurationV2Request>({
      query: (request: createFlexibleReportConfigurationV2Request) => {
        return {
          url: `/adsApi/v1/create/reportConfigurations`,
          method: HttpMethod.POST,
          headers: { ...getFrbAuthorizationHeaders(), ...getMarketplaceIdHeader(), ...getLocaleheader() },
          data: request
        };
      },
      transformResponse: transformCreateReportSuccessResponse,
      transformErrorResponse: (error: ApiErrorResponse): ServerValidationError =>
        createServerError(mapApiErrorToServerCode(error), error.status, error)
    })
  })
});

export const {
  useLazyListFlexibleReportConfigurationsQuery,
  useListFlexibleReportConfigurationsQuery,
  useCreateFlexibleReportConfigurationMutation,
  useGetFlexibleReportConfigurationQuery,
  useLazyGetFlexibleReportConfigurationQuery,
  useUpdateFlexibleReportConfigurationMutation,
  useUpdateFlexibleReportConfigurationStatusMutation,
  useDeleteFlexibleReportConfigurationMutation,
  useCreateFlexibleReportConfigurationV2Mutation
} = flexibleSubscriptionsApi;
